import MicroModal from 'micromodal'
import type { Activity, Page } from '@/scripts/data/types'
import Analytics, { ACTION_ACTIVITY_CLOSE } from '@/scripts/analytics'
import audioManager from '@/scripts/audio/audioManager'
import { clearHash } from '@/scripts/routing/hashCheck'

type OpenOpts = {
  onShow?: () => void
  onClose?: () => void
}

let closeButtonHandler: ((e: Event) => void) | null = null

/**
 * Type guard to check if the object is an Activity
 */
function isActivity(obj: Activity | Page): obj is Activity {
  return 'day' in obj && 'positionId' in obj
}

/**
 * Opens the shared MicroModal (#modal-activity) with the Activity or Page iframe.
 * Used on BOTH desktop (Scene.ts) and mobile (App.ts via createActivityView).
 */
export function openActivityModal(activityOrPage: Activity | Page, opts: OpenOpts = {}) {
  const modalRoot = document.getElementById('modal-activity') as HTMLElement | null
  const iframeEl = document.getElementById('iframe') as HTMLIFrameElement | null
  const overlayEl = modalRoot?.querySelector('.modal__overlay') as HTMLElement | null
  const containerEl = modalRoot?.querySelector('.modal__container') as HTMLElement | null
  const closeButton = modalRoot?.querySelector('.modal__close-btn') as HTMLButtonElement | null

  if (!modalRoot || !iframeEl || !overlayEl || !containerEl) {
    console.warn('[openActivity] #modal-activity structure missing in index.html')
    return
  }

  // Clicking on overlay should not close the modal
  overlayEl.removeAttribute('data-micromodal-close')

  // Inject iframe content
  iframeEl.src = ''
  const url = isActivity(activityOrPage) ? activityOrPage.url : (activityOrPage.url ?? '')
  iframeEl.src = url

  // Only these two modals should have rounded corners (design decision)
  const linkHash = isActivity(activityOrPage) ? activityOrPage.linkHash : activityOrPage.linkHash
  if (linkHash && ['o-projektu', 'soutez'].includes(linkHash)) {
    iframeEl.classList.add('rounded')
  } else {
    iframeEl.classList.remove('rounded')
  }

  // Clean up previous handlers if they exist
  if (closeButtonHandler && closeButton) {
    closeButton.removeEventListener('click', closeButtonHandler)
    closeButtonHandler = null
  }

  // Intercept close button with confirmation dialog
  const confirmOnClose = isActivity(activityOrPage) ? activityOrPage.confirmOnClose : activityOrPage.confirmOnClose
  if (closeButton && confirmOnClose !== false) {
    // Remove MicroModal's default close behavior
    closeButton.removeAttribute('data-micromodal-close')

    // Add custom close handler with confirmation
    closeButtonHandler = (e: Event) => {
      e.preventDefault()
      e.stopPropagation()

      if (window.confirm('Opravdu chceš zavřít okno s aktivitou?')) {
        MicroModal.close('modal-activity')
      }
    }
    closeButton.addEventListener('click', closeButtonHandler)
  } else if (closeButton) {
    // Restore MicroModal's default close behavior for modals without confirmation
    closeButton.setAttribute('data-micromodal-close', '')
  }

  MicroModal.show('modal-activity', {
    onShow: () => {
      // Global audio mute while iframe modal is open
      audioManager.setMutedManually(true)
      opts.onShow?.()
    },
    onClose: () => {
      // Clean up handlers
      if (closeButtonHandler && closeButton) {
        closeButton.removeEventListener('click', closeButtonHandler)
        closeButtonHandler = null
      }

      iframeEl.src = ''
      clearHash()
      audioManager.setMutedManually(false)
      const linkHash = isActivity(activityOrPage) ? activityOrPage.linkHash : activityOrPage.linkHash
      if (linkHash) {
        Analytics.sendCustomEvent(ACTION_ACTIVITY_CLOSE, linkHash)
      }
      opts.onClose?.()
    },
  })
}

/**
 * Cleans up the activity modal iframe by clearing its src.
 * Used when switching between mobile/desktop modes.
 */
export function cleanupActivityIframe() {
  const iframeEl = document.getElementById('iframe') as HTMLIFrameElement | null
  if (iframeEl) {
    iframeEl.src = ''
  }
}
